/**
 * ProductContentReview
 *
 * Product content review dashboard (pure code move from ContentReviewDashboard).
 * Shows metrics, filterable queue, and review panel for product content.
 *
 * Part of Story 29: Content Review Dashboard with Reviewer Attribution
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import {
  Filter,
  RefreshCw,
  FileText,
  Tag,
  FolderTree,
  Calendar,
  CheckSquare,
  CheckCircle,
  Square,
  Loader2,
  Search,
  X,
  Archive
} from 'lucide-react';
import { format, subDays, startOfDay, endOfDay, startOfYear } from 'date-fns';
import { useSite } from '@/contexts/SiteContext';
import { ReviewMetrics, TopReviewers } from '@/components/content-review/ReviewMetrics';
import { ContentQueue } from '@/components/content-review/ContentQueue';
import { ReviewPanel } from '@/components/content-review/ReviewPanel';
import {
  getMetrics,
  getQueue,
  getQueueIds,
  getContentDetails,
  bulkApprove,
  bulkRegenerate,
  getProductCategories,
  ReviewMetrics as MetricsData,
  QueueItem,
  QueueFilters,
  CategoryWithCount,
  BulkRegenerateResult
} from '@/services/contentReviewService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const CONTENT_TYPES = [
  { value: 'all', label: 'All Types', icon: null },
  { value: 'ai_article', label: 'AI Articles', icon: FileText },
  { value: 'product_aeo', label: 'Product AEO', icon: Tag },
  { value: 'category_aeo', label: 'Category AEO', icon: FolderTree },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'needs_research', label: 'Needs Research' },
];

const FAQ_QA_OPTIONS = [
  { value: 'all', label: 'All FAQ Status' },
  { value: 'passed', label: 'FAQ QA Passed' },
  { value: 'failed', label: 'FAQ QA Failed' },
];

const PREVIOUS_REJECTION_OPTIONS = [
  { value: 'all', label: 'All Items' },
  { value: 'with_rejection', label: 'Previously Rejected' },
  { value: 'without_rejection', label: 'No Previous Rejection' },
];

const MANUAL_REVIEW_OPTIONS = [
  { value: 'all', label: 'All Items' },
  { value: 'needs_review', label: 'Needs Manual Review' },
  { value: 'no_review', label: 'No Review Needed' },
];

const DATE_PRESET_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'this_year', label: 'This year' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

// Helper to get date range from preset
function getDateRangeFromPreset(preset: string): { from: Date; to: Date } | null {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'yesterday':
      const yesterday = subDays(now, 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    case 'last_7_days':
      return { from: startOfDay(subDays(now, 7)), to: endOfDay(now) };
    case 'last_30_days':
      return { from: startOfDay(subDays(now, 30)), to: endOfDay(now) };
    case 'this_year':
      return { from: startOfYear(now), to: endOfDay(now) };
    case 'all_time':
      return null; // No date filter
    default:
      return null;
  }
}

export function ProductContentReview() {
  const { selectedSite } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();

  // Metrics state
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Queue state
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 25
  });

  // Filter state
  const [filters, setFilters] = useState<QueueFilters>({
    siteId: selectedSite?.id || null,
    contentType: null,
    status: 'pending',
    startDate: subDays(new Date(), 30).toISOString(),
    endDate: endOfDay(new Date()).toISOString(),
    page: 1,
    limit: 25,
    sortBy: 'updated_at',
    sortOrder: 'DESC'
  });

  // Review panel state
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [reviewPanelOpen, setReviewPanelOpen] = useState(false);

  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [allItemsSelected, setAllItemsSelected] = useState(false); // Select all across pages
  const [loadingAllIds, setLoadingAllIds] = useState(false);
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);
  const [bulkRegenerateLoading, setBulkRegenerateLoading] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerateMode, setRegenerateMode] = useState<'batch' | 'realtime'>('batch');
  const [faqQaFilter, setFaqQaFilter] = useState<string>('all');
  const [previousRejectionFilter, setPreviousRejectionFilter] = useState<string>('all');
  const [manualReviewFilter, setManualReviewFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [urlInput, setUrlInput] = useState<string>('');
  const [urlDebounceTimer, setUrlDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [categories, setCategories] = useState<CategoryWithCount[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false); // Show products no longer in active feed
  const [showPublished, setShowPublished] = useState(false); // Show published content

  // Date range state for filter
  const [datePreset, setDatePreset] = useState<string>('last_30_days');
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: startOfDay(subDays(new Date(), 30)),
    to: endOfDay(new Date())
  });

  // Handle URL params for direct linking (only when navigating directly, not from queue)
  useEffect(() => {
    const id = searchParams.get('id');
    const type = searchParams.get('type');

    // Only load from URL if we don't already have a selected item
    // (prevents overwriting when clicking Review from queue)
    if (id && type && !selectedItem) {
      loadItemFromUrl(type, id);
    }
  }, [searchParams]);

  // Update site filter when site changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      siteId: selectedSite?.id || null,
      category: null // Reset category filter when site changes
    }));
    setCategoryFilter('all');
  }, [selectedSite?.id]);

  // Load categories when site changes
  useEffect(() => {
    const loadCategories = async () => {
      if (!selectedSite?.id) {
        setCategories([]);
        return;
      }
      setCategoriesLoading(true);
      try {
        const response = await getProductCategories(selectedSite.id);
        if (response.success) {
          setCategories(response.data);
        }
      } catch (err) {
        console.error('[ContentReviewDashboard] Error loading categories:', err);
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    loadCategories();
  }, [selectedSite?.id]);

  // Load data when filters change
  useEffect(() => {
    loadMetrics();
    loadQueue();
    // Reset "all items selected" when filters change (the "all" now refers to different items)
    setAllItemsSelected(false);
  }, [filters]);

  const loadItemFromUrl = async (type: string, id: string) => {
    try {
      // CRITICAL: Pass siteId for multi-tenancy security
      if (!selectedSite?.id) {
        console.warn('[ContentReviewDashboard] No site selected, cannot load item');
        return;
      }
      const response = await getContentDetails(type, id, selectedSite.id);
      if (response.success) {
        // Backend already normalizes status to 'approved'/'rejected'/'pending'
        // No need to re-transform
        const normalizedStatus = response.data.status === 'approved' || response.data.status === 'published' ? 'approved' :
                                 response.data.status === 'rejected' || response.data.status === 'archived' ? 'rejected' : 'pending';
        const item: QueueItem = {
          id: response.data.id,
          title: response.data.title,
          contentType: response.data.contentType,
          status: normalizedStatus,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt || response.data.createdAt,
          siteId: response.data.siteId,
          siteName: response.data.siteName,
          approvedBy: response.data.approvedBy || null,
          reviewerName: response.data.reviewerName || null,
          approvedAt: response.data.approvedAt || null,
          rejectionReason: response.data.rejectionReason || null,
          faqQaPassed: null,
          faqCount: null,
          previousRejection: null,
          needsManualReview: false,
          category: response.data.metadata?.category as string || null
        };
        setSelectedItem(item);
        setReviewPanelOpen(true);
      }
    } catch (err) {
      console.error('[ContentReviewDashboard] Error loading item from URL:', err);
      toast({
        title: 'Error',
        description: 'Failed to load the requested content',
        variant: 'destructive',
      });
    }
  };

  const loadMetrics = async () => {
    // CRITICAL: Require siteId for multi-tenancy security
    if (!filters.siteId) {
      setMetrics(null);
      return;
    }

    setMetricsLoading(true);

    try {
      const response = await getMetrics({
        siteId: filters.siteId,
        startDate: filters.startDate,
        endDate: filters.endDate
      });

      if (response.success) {
        setMetrics(response.data);
      }
    } catch (err) {
      console.error('[ContentReviewDashboard] Error loading metrics:', err);
      toast({
        title: 'Error',
        description: 'Failed to load metrics',
        variant: 'destructive',
      });
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadQueue = async () => {
    // CRITICAL: Require siteId for multi-tenancy security
    if (!filters.siteId) {
      setQueue([]);
      setPagination({ page: 1, totalPages: 0, total: 0, limit: 25 });
      return;
    }

    setQueueLoading(true);

    try {
      const response = await getQueue(filters);

      if (response.success) {
        setQueue(response.data.items);
        setPagination({
          page: response.data.page,
          totalPages: response.data.totalPages,
          total: response.data.total,
          limit: response.data.limit
        });
      }
    } catch (err) {
      console.error('[ContentReviewDashboard] Error loading queue:', err);
      toast({
        title: 'Error',
        description: 'Failed to load content queue',
        variant: 'destructive',
      });
    } finally {
      setQueueLoading(false);
    }
  };

  const handleRefresh = () => {
    loadMetrics();
    loadQueue();
  };

  const handleContentTypeChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      contentType: value === 'all' ? null : value,
      page: 1
    }));
  };

  const handleStatusChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      status: value === 'all' ? null : value,
      page: 1
    }));
  };

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset);

    if (preset === 'custom') {
      // Keep current date range when switching to custom
      return;
    }

    const range = getDateRangeFromPreset(preset);
    if (range) {
      setDateRange(range);
      setFilters(prev => ({
        ...prev,
        startDate: range.from.toISOString(),
        endDate: range.to.toISOString(),
        page: 1
      }));
    } else {
      // all_time - no date filter
      setFilters(prev => ({
        ...prev,
        startDate: null,
        endDate: null,
        page: 1
      }));
    }
  };

  const handleDateRangeChange = (range: { from: Date; to: Date }) => {
    setDateRange(range);
    setFilters(prev => ({
      ...prev,
      startDate: range.from.toISOString(),
      endDate: endOfDay(range.to).toISOString(),
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSortChange = (column: 'updated_at' | 'title' | 'content_type') => {
    setFilters(prev => {
      // If clicking the same column, toggle the order
      if (prev.sortBy === column) {
        return {
          ...prev,
          sortOrder: prev.sortOrder === 'ASC' ? 'DESC' : 'ASC',
          page: 1
        };
      }
      // New column - default to DESC for date, ASC for others
      return {
        ...prev,
        sortBy: column,
        sortOrder: column === 'updated_at' ? 'DESC' : 'ASC',
        page: 1
      };
    });
  };

  const handleReview = (item: QueueItem) => {
    setSelectedItem(item);
    setReviewPanelOpen(true);
    // Update URL for direct linking
    setSearchParams({ id: item.id, type: item.contentType });
  };

  const handleReviewPanelClose = () => {
    setReviewPanelOpen(false);
    setSelectedItem(null);
    // Clear URL params
    setSearchParams({});
  };

  const handleActionComplete = () => {
    // Refresh data after approve/reject
    loadMetrics();
    loadQueue();
  };

  const handleFaqQaFilterChange = (value: string) => {
    setFaqQaFilter(value);
    setFilters(prev => ({
      ...prev,
      faqQaPassed: value === 'passed' ? true : value === 'failed' ? false : null,
      page: 1
    }));
  };

  const handlePreviousRejectionFilterChange = (value: string) => {
    setPreviousRejectionFilter(value);
    setFilters(prev => ({
      ...prev,
      hasPreviousRejection: value === 'with_rejection' ? true : value === 'without_rejection' ? false : null,
      page: 1
    }));
  };

  const handleManualReviewFilterChange = (value: string) => {
    setManualReviewFilter(value);
    setFilters(prev => ({
      ...prev,
      needsManualReview: value === 'needs_review' ? true : value === 'no_review' ? false : null,
      page: 1
    }));
  };

  const handleCategoryFilterChange = (value: string) => {
    setCategoryFilter(value);
    setFilters(prev => ({
      ...prev,
      category: value === 'all' ? null : value,
      page: 1
    }));
  };

  const handleShowInactiveChange = (checked: boolean) => {
    setShowInactive(checked);
    setFilters(prev => ({
      ...prev,
      includeInactive: checked,
      page: 1
    }));
  };

  const handleShowPublishedChange = (checked: boolean) => {
    setShowPublished(checked);
    setFilters(prev => ({
      ...prev,
      includePublished: checked,
      page: 1
    }));
  };

  // Helper to get the last segment of a category path (e.g., "Bikes > Road > Racing" → "Racing")
  const getCategoryShortName = (categoryPath: string): string => {
    const parts = categoryPath.split(' > ');
    return parts[parts.length - 1] || categoryPath;
  };

  // Select all items on current page that don't need manual review (safe for bulk approval)
  const handleSelectAllSafe = () => {
    const safeItems = queue
      .filter(item => !item.needsManualReview && item.status === 'pending')
      .map(item => `${item.contentType}:${item.id}`);
    setSelectedItems(new Set(safeItems));
    setAllItemsSelected(false);
  };

  // Select ALL items across all pages matching current filters
  const selectAllItemsAcrossPages = async () => {
    if (!filters.siteId) return;

    setLoadingAllIds(true);
    try {
      const response = await getQueueIds({
        siteId: filters.siteId,
        contentType: filters.contentType,
        status: filters.status,
        startDate: filters.startDate,
        endDate: filters.endDate,
        reviewerId: null,
        faqQaPassed: filters.faqQaPassed,
        hasPreviousRejection: filters.hasPreviousRejection,
        needsManualReview: filters.needsManualReview,
        search: filters.search,
        urlContains: filters.urlContains,
        category: filters.category,
        includeInactive: filters.includeInactive,
      });

      if (response.success) {
        const allIds = response.data.ids.map(item => `${item.contentType}:${item.id}`);
        setSelectedItems(new Set(allIds));
        setAllItemsSelected(true);
        toast({
          title: 'All Items Selected',
          description: `Selected ${allIds.length} items across all pages.${response.data.truncated ? ' (limited to 10,000)' : ''}`,
        });
      }
    } catch (error) {
      console.error('[ContentReviewDashboard] Error fetching all item IDs:', error);
      toast({
        title: 'Error',
        description: 'Failed to select all items.',
        variant: 'destructive',
      });
    } finally {
      setLoadingAllIds(false);
    }
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedItems(new Set());
    setAllItemsSelected(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    // Debounce the search to avoid too many API calls
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    const timer = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        search: value.trim() || null,
        page: 1
      }));
    }, 300);
    setSearchDebounceTimer(timer);
  };

  const clearSearch = () => {
    setSearchInput('');
    setFilters(prev => ({
      ...prev,
      search: null,
      page: 1
    }));
  };

  const handleUrlContainsChange = (value: string) => {
    setUrlInput(value);
    // Debounce identically to the search field to avoid spam on every keystroke
    if (urlDebounceTimer) {
      clearTimeout(urlDebounceTimer);
    }
    const timer = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        urlContains: value.trim() || null,
        page: 1
      }));
    }, 300);
    setUrlDebounceTimer(timer);
  };

  const clearUrlContains = () => {
    setUrlInput('');
    setFilters(prev => ({
      ...prev,
      urlContains: null,
      page: 1
    }));
  };

  const handleSelectionChange = (newSelected: Set<string>) => {
    setSelectedItems(newSelected);
    // Reset "all items selected" when manually changing selection
    setAllItemsSelected(false);
  };

  const handleBulkApprove = async () => {
    if (selectedItems.size === 0) {
      toast({
        title: 'No items selected',
        description: 'Please select at least one item to approve',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedSite?.id) {
      toast({
        title: 'No site selected',
        description: 'Please select a site first',
        variant: 'destructive',
      });
      return;
    }

    setBulkApproveLoading(true);
    try {
      // Convert Set to array of {contentType, id} objects
      const items = Array.from(selectedItems).map(key => {
        const [contentType, id] = key.split(':');
        return { contentType, id };
      });

      const result = await bulkApprove(items, selectedSite.id);

      if (result.success) {
        toast({
          title: 'Bulk Approval Complete',
          description: result.message,
        });
        setSelectedItems(new Set());
        setAllItemsSelected(false);
        handleRefresh();
      }

      if (result.failed.length > 0) {
        console.warn('[ContentReviewDashboard] Some items failed to approve:', result.failed);
      }
    } catch (err) {
      console.error('[ContentReviewDashboard] Bulk approve error:', err);
      toast({
        title: 'Error',
        description: 'Failed to bulk approve content',
        variant: 'destructive',
      });
    } finally {
      setBulkApproveLoading(false);
    }
  };

  const handleOpenRegenerateDialog = () => {
    if (selectedItems.size === 0) {
      toast({
        title: 'No items selected',
        description: 'Please select at least one item to regenerate',
        variant: 'destructive',
      });
      return;
    }

    // Count product_aeo items
    const productAeoCount = Array.from(selectedItems).filter(key =>
      key.startsWith('product_aeo:')
    ).length;

    if (productAeoCount === 0) {
      toast({
        title: 'No product AEO items selected',
        description: 'Bulk regeneration only supports product AEO items',
        variant: 'destructive',
      });
      return;
    }

    // Default to batch for > 10 items
    if (productAeoCount > 10) {
      setRegenerateMode('batch');
    }

    setRegenerateDialogOpen(true);
  };

  const handleBulkRegenerate = async () => {
    if (!selectedSite?.id) {
      toast({
        title: 'No site selected',
        description: 'Please select a site first',
        variant: 'destructive',
      });
      return;
    }

    setBulkRegenerateLoading(true);
    try {
      // Convert Set to array of {contentType, id} objects
      const items = Array.from(selectedItems).map(key => {
        const [contentType, id] = key.split(':');
        return { contentType, id };
      });

      const result = await bulkRegenerate(items, regenerateMode, selectedSite.id);

      if (result.success) {
        setRegenerateDialogOpen(false);
        setSelectedItems(new Set());
        setAllItemsSelected(false);

        if (result.mode === 'batch') {
          toast({
            title: 'Batch Regeneration Started',
            description: `${result.totalProducts} products queued for regeneration. Check Feed Import Status for progress.`,
          });
        } else {
          toast({
            title: 'Regeneration Complete',
            description: result.message,
          });
          handleRefresh();
        }
      }
    } catch (err) {
      console.error('[ContentReviewDashboard] Bulk regenerate error:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to bulk regenerate content',
        variant: 'destructive',
      });
    } finally {
      setBulkRegenerateLoading(false);
    }
  };

  // Count product_aeo items in selection
  const selectedProductAeoCount = Array.from(selectedItems).filter(key =>
    key.startsWith('product_aeo:')
  ).length;

  // Check if all visible items (on current page) are selected
  const allVisibleSelected = queue.length > 0 && queue.every(item =>
    selectedItems.has(`${item.contentType}:${item.id}`)
  );

  // Check if there are more items across pages
  const hasMoreItemsAcrossPages = pagination.total > queue.length;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Content Review Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Review and approve AI-generated content
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Metrics */}
      <ReviewMetrics metrics={metrics} loading={metricsLoading} />

      {/* Top Reviewers */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="md:col-span-2">
          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {/* Search Input */}
                <div className="flex-1 min-w-[250px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Search Product
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by product name or SKU..."
                      value={searchInput}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="pl-9 pr-9"
                    />
                    {searchInput && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* URL Filter — products only; matches a substring of the product URL */}
                <div className="flex-1 min-w-[250px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Filter by URL
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Paste a product URL or its embedded ID..."
                      value={urlInput}
                      onChange={(e) => handleUrlContainsChange(e.target.value)}
                      className="pl-9 pr-9"
                    />
                    {urlInput && (
                      <button
                        onClick={clearUrlContains}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Content Type Filter */}
                <div className="flex-1 min-w-[180px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Content Type
                  </label>
                  <Select
                    value={filters.contentType || 'all'}
                    onValueChange={handleContentTypeChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            {type.icon && <type.icon className="h-4 w-4" />}
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Status
                  </label>
                  <Select
                    value={filters.status || 'all'}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* FAQ QA Filter */}
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    FAQ QA Status
                  </label>
                  <Select
                    value={faqQaFilter}
                    onValueChange={handleFaqQaFilterChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select FAQ status" />
                    </SelectTrigger>
                    <SelectContent>
                      {FAQ_QA_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Previous Rejection Filter */}
                <div className="flex-1 min-w-[170px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Rejection History
                  </label>
                  <Select
                    value={previousRejectionFilter}
                    onValueChange={handlePreviousRejectionFilterChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select rejection history" />
                    </SelectTrigger>
                    <SelectContent>
                      {PREVIOUS_REJECTION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Manual Review Filter */}
                <div className="flex-1 min-w-[170px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Review Status
                  </label>
                  <Select
                    value={manualReviewFilter}
                    onValueChange={handleManualReviewFilterChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select review status" />
                    </SelectTrigger>
                    <SelectContent>
                      {MANUAL_REVIEW_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Filter */}
                <div className="flex-1 min-w-[180px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Category
                  </label>
                  <Select
                    value={categoryFilter}
                    onValueChange={handleCategoryFilterChange}
                    disabled={categoriesLoading || categories.length === 0}
                  >
                    <SelectTrigger>
                      <FolderTree className="h-4 w-4 mr-2 opacity-50" />
                      <SelectValue placeholder={categoriesLoading ? "Loading..." : "Select category"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.category} value={cat.category}>
                          <span title={cat.category}>
                            {getCategoryShortName(cat.category)} ({cat.productCount})
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range Filter */}
                <div className="flex-1 min-w-[180px]">
                  <label className="text-sm font-medium mb-1.5 block">
                    Date Range
                  </label>
                  <Select
                    value={datePreset}
                    onValueChange={handleDatePresetChange}
                  >
                    <SelectTrigger>
                      <Calendar className="h-4 w-4 mr-2 opacity-50" />
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_PRESET_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Show Inactive Products Toggle */}
                <div className="flex items-center min-w-[180px] pt-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="show-inactive"
                      checked={showInactive}
                      onCheckedChange={handleShowInactiveChange}
                    />
                    <Label
                      htmlFor="show-inactive"
                      className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                    >
                      <Archive className="h-4 w-4 text-muted-foreground" />
                      Show Inactive
                    </Label>
                  </div>
                </div>

                {/* Show Published Toggle */}
                <div className="flex items-center min-w-[180px] pt-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="show-published"
                      checked={showPublished}
                      onCheckedChange={handleShowPublishedChange}
                    />
                    <Label
                      htmlFor="show-published"
                      className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                    >
                      <CheckCircle className="h-4 w-4 text-muted-foreground" />
                      Show Published
                    </Label>
                  </div>
                </div>

                {/* Custom Date Inputs - only show when custom is selected */}
                {datePreset === 'custom' && (
                  <div className="flex-1 min-w-[300px]">
                    <label className="text-sm font-medium mb-1.5 block">
                      Custom Range
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={format(dateRange.from, 'yyyy-MM-dd')}
                        onChange={(e) => {
                          const newDate = new Date(e.target.value);
                          if (!isNaN(newDate.getTime())) {
                            handleDateRangeChange({
                              from: startOfDay(newDate),
                              to: dateRange.to
                            });
                          }
                        }}
                        className="flex-1"
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="date"
                        value={format(dateRange.to, 'yyyy-MM-dd')}
                        onChange={(e) => {
                          const newDate = new Date(e.target.value);
                          if (!isNaN(newDate.getTime())) {
                            handleDateRangeChange({
                              from: dateRange.from,
                              to: newDate
                            });
                          }
                        }}
                        className="flex-1"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Active filters summary */}
              <div className="flex flex-wrap gap-2 mt-4">
                {filters.search && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    Search: {filters.search}
                  </Badge>
                )}
                {filters.contentType && (
                  <Badge variant="secondary">
                    Type: {CONTENT_TYPES.find(t => t.value === filters.contentType)?.label}
                  </Badge>
                )}
                {filters.status && (
                  <Badge variant="secondary">
                    Status: {STATUS_OPTIONS.find(s => s.value === filters.status)?.label}
                  </Badge>
                )}
                {faqQaFilter !== 'all' && (
                  <Badge variant="secondary">
                    FAQ: {FAQ_QA_OPTIONS.find(o => o.value === faqQaFilter)?.label}
                  </Badge>
                )}
                {previousRejectionFilter !== 'all' && (
                  <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                    {PREVIOUS_REJECTION_OPTIONS.find(o => o.value === previousRejectionFilter)?.label}
                  </Badge>
                )}
                {manualReviewFilter !== 'all' && (
                  <Badge variant="secondary" className={manualReviewFilter === 'needs_review' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                    {MANUAL_REVIEW_OPTIONS.find(o => o.value === manualReviewFilter)?.label}
                  </Badge>
                )}
                {categoryFilter !== 'all' && (
                  <Badge variant="secondary" className="bg-purple-100 text-purple-800" title={categoryFilter}>
                    Category: {getCategoryShortName(categoryFilter)}
                  </Badge>
                )}
                {showInactive && (
                  <Badge variant="secondary" className="bg-gray-200 text-gray-700">
                    <Archive className="h-3 w-3 mr-1" />
                    Including Inactive
                  </Badge>
                )}
                <Badge variant="secondary">
                  {datePreset === 'custom'
                    ? `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`
                    : DATE_PRESET_OPTIONS.find(o => o.value === datePreset)?.label
                  }
                </Badge>
                {selectedSite && (
                  <Badge variant="outline">
                    Site: {selectedSite.name || selectedSite.url}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Reviewers */}
        <TopReviewers
          reviewers={metrics?.topReviewers || []}
          loading={metricsLoading}
        />
      </div>

      {/* Content Queue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>Content Queue</span>
              <Badge variant="secondary">{pagination.total} items</Badge>
              {selectedItems.size > 0 && (
                <Badge variant="outline">
                  {selectedItems.size} selected
                  {allItemsSelected && <span className="font-medium text-primary"> (all items)</span>}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Select all items on page that don't need manual review */}
              {queue.some(item => !item.needsManualReview && item.status === 'pending') && !allVisibleSelected && (
                <Button
                  variant="outline"
                  onClick={handleSelectAllSafe}
                  disabled={bulkApproveLoading || loadingAllIds}
                  size="sm"
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Select Safe Items
                </Button>
              )}

              {/* Select ALL items across pages - show when all visible are selected but more exist */}
              {allVisibleSelected && !allItemsSelected && hasMoreItemsAcrossPages && (
                <Button
                  variant="outline"
                  onClick={selectAllItemsAcrossPages}
                  disabled={loadingAllIds || bulkApproveLoading}
                  size="sm"
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  {loadingAllIds ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Select All {pagination.total} Items
                    </>
                  )}
                </Button>
              )}

              {/* Clear Selection */}
              {selectedItems.size > 0 && (
                <Button
                  variant="outline"
                  onClick={clearSelection}
                  disabled={bulkApproveLoading || bulkRegenerateLoading}
                  size="sm"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Clear Selection
                </Button>
              )}

              {selectedItems.size > 0 && (
                <>
                  <Button
                    onClick={handleBulkApprove}
                    disabled={bulkApproveLoading || bulkRegenerateLoading}
                    size="sm"
                  >
                    {bulkApproveLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckSquare className="h-4 w-4 mr-2" />
                        Approve Selected ({selectedItems.size})
                      </>
                    )}
                  </Button>
                  {selectedProductAeoCount > 0 && (
                    <Button
                      onClick={handleOpenRegenerateDialog}
                      disabled={bulkApproveLoading || bulkRegenerateLoading}
                      variant="outline"
                      size="sm"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate ({selectedProductAeoCount})
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ContentQueue
            key={`queue-${pagination.total}-${filters.category || 'all'}-${queue[0]?.id || 'empty'}`}
            items={queue}
            loading={queueLoading}
            onReview={handleReview}
            pagination={pagination}
            onPageChange={handlePageChange}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
            showSelection={true}
            sortBy={filters.sortBy as 'updated_at' | 'title' | 'content_type'}
            sortOrder={filters.sortOrder as 'ASC' | 'DESC'}
            onSortChange={handleSortChange}
          />
        </CardContent>
      </Card>

      {/* Review Panel */}
      <ReviewPanel
        open={reviewPanelOpen}
        onClose={handleReviewPanelClose}
        item={selectedItem}
        onActionComplete={handleActionComplete}
      />

      {/* Bulk Regenerate Dialog */}
      <Dialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Regenerate Selected Products</DialogTitle>
            <DialogDescription>
              Regenerate AEO content for {selectedProductAeoCount} selected product{selectedProductAeoCount !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <RadioGroup
              value={regenerateMode}
              onValueChange={(value) => setRegenerateMode(value as 'batch' | 'realtime')}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="batch" id="batch" className="mt-1" />
                <Label htmlFor="batch" className="flex-1 cursor-pointer">
                  <div className="font-medium">Batch Mode (Recommended)</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Uses batch processing for ~50% cost savings. Processing completes within ~24 hours.
                    Uses your configured LLM model from Content Settings.
                  </div>
                </Label>
              </div>

              <div className={`flex items-start space-x-3 p-3 border rounded-lg ${
                selectedProductAeoCount > 10
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-muted/50 cursor-pointer'
              }`}>
                <RadioGroupItem
                  value="realtime"
                  id="realtime"
                  className="mt-1"
                  disabled={selectedProductAeoCount > 10}
                />
                <Label
                  htmlFor="realtime"
                  className={`flex-1 ${selectedProductAeoCount > 10 ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="font-medium">Real-time Mode</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {selectedProductAeoCount > 10 ? (
                      <span className="text-amber-600">
                        Limited to 10 products to prevent timeout. Select fewer items or use batch mode.
                      </span>
                    ) : (
                      'Processes immediately. Best for small selections when you need results right away.'
                    )}
                  </div>
                </Label>
              </div>
            </RadioGroup>

            {regenerateMode === 'batch' && (
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  After starting, you can track progress on the Feed Import Status page.
                  Products will appear in the Content Queue once regeneration completes.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRegenerateDialogOpen(false)}
              disabled={bulkRegenerateLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkRegenerate}
              disabled={bulkRegenerateLoading}
            >
              {bulkRegenerateLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {regenerateMode === 'batch' ? 'Starting...' : 'Regenerating...'}
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {regenerateMode === 'batch' ? 'Start Batch' : 'Regenerate Now'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
